function setLangeModuleTitle(fallback){const title=window.LANGE_NATIVE_SEO?.title||fallback;if(document.title!==title)document.title=title;}
import { header, footer, bindHeader, PAGE_PATHS, CONCERN_PATH } from "./_lange-catalogue--chrome.js";
import { HOME_PATH, escapeHtml } from "./_lange-catalogue--set-builder-core.js";
import { mobilePhoto } from "./_lange-catalogue--responsive-images.js";
const JOURNAL_PATH = '/version-1-tracked/en/journal';
const JOURNAL_LINK = `${JOURNAL_PATH}/`;
const CDN_ORIGIN = 'https://lange-paris.netlify.app';
const categories = [{
  id: 'all',
  label: 'All notes'
}, {
  id: 'fine-lines',
  label: 'Face fine lines & skin firmness'
}, {
  id: 'eye-area',
  label: 'The Eye area'
}, {
  id: 'hydration',
  label: 'Hydration'
}, {
  id: 'ingredients',
  label: 'Ingredients'
}, {
  id: 'routine',
  label: 'Routine notes'
}];
const originalArticles = [{
  slug: 'a-simple-routine-for-fine-lines',
  category: 'fine-lines',
  label: 'Face fine lines & skin firmness',
  title: 'A simple routine for fine lines',
  deck: 'A clear place to begin when you want care that feels considered, not complicated.',
  excerpt: 'Fine lines do not call for a crowded shelf. Start with a few steps you can use well and keep using.',
  readTime: '4 min read',
  date: '10 August 2026',
  isoDate: '2026-08-10',
  image: '/lange/paris/editorial/portrait-fine-lines-1600.webp',
  imageAlt: 'Woman with natural skin in a white shirt near limestone',
  concernUrl: `${CONCERN_PATH}/fine-lines/`,
  concernLabel: 'Explore care for fine lines & firmness',
  sections: [{
    heading: 'Start with what you see',
    paragraphs: ['Fine lines often show up first around the eyes, mouth and forehead. They can look more visible when skin feels dry, tired or short on moisture. That does not mean you need a long routine.', 'Begin by looking for the moment you notice them most. Is it after cleansing? By late afternoon? Under make-up? The answer helps you choose care that fits your skin instead of adding steps at random.']
  }, {
    heading: 'Keep the order simple',
    paragraphs: ['In the morning, cleanse gently if you need to, then use a Serum or light treatment, eye care and a day cream. At night, take the day off your skin, then use the treatment and cream you enjoy using most.', 'The order matters less than being able to repeat it. A product that sits unused cannot help your skin feel more comfortable or look more hydrated.'],
    steps: ['Use light layers before richer ones.', 'Use a small amount around The Eye area.', 'Give each layer a short moment to settle.']
  }, {
    heading: 'Choose for comfort, then stay consistent',
    paragraphs: ['A finish that feels too heavy or too tight is a reason to stop, not something to push through. Notice texture, scent and how your skin feels later in the day. Those details are part of finding a routine you can keep.', 'If your skin is suddenly sore, swollen or persistently irritated, pause new products and speak with a qualified health professional. Skincare should not ask you to ignore discomfort.']
  }]
}, {
  slug: 'eye-care-where-to-start',
  category: 'eye-area',
  label: 'The Eye area',
  title: 'The Eye area: where to start',
  deck: 'The skin around the eyes is delicate. A small, gentle step can be enough.',
  excerpt: 'What to look for in eye care, how much to use, and why a lighter touch is usually better.',
  readTime: '3 min read',
  date: '28 July 2026',
  isoDate: '2026-07-28',
  image: '/lange/paris/editorial/eye-ritual-1400.webp',
  imageAlt: 'LANGÉ eye-care ritual and packaging in warm natural light',
  concernUrl: `${CONCERN_PATH}/eye-area/`,
  concernLabel: 'Explore care for The Eye area',
  sections: [{
    heading: 'Why this area feels different',
    paragraphs: ['The skin around your eyes is thinner than the skin on your cheeks. It also moves all day when you blink, smile and squint. That is why dryness and fine lines can seem more noticeable there.', 'Eye care is not about doing more. It is about using a formula that feels comfortable in a very small area and applying it with care.']
  }, {
    heading: 'How much is enough',
    paragraphs: ['Start with an amount no larger than a grain of rice for both eyes. Warm it between your ring fingers, then press it lightly around the orbital bone. Avoid rubbing close to the lash line.', 'If a product stings, makes your eyes water or leaves the area sore, stop using it. Comfortable skin is the goal.'],
    steps: ['Use your ring finger for the lightest pressure.', 'Press; do not drag.', 'Keep the rest of your routine simple around the eyes.']
  }, {
    heading: 'Make it a useful habit',
    paragraphs: ['Keep your eye product beside the step you already remember, such as your moisturiser. A small daily habit is more useful than a complicated plan you only do once a week.']
  }]
}, {
  slug: 'hydration-what-skin-needs',
  category: 'hydration',
  label: 'Hydration',
  title: 'When skin feels dry, what does it need?',
  deck: 'Dry, tight skin is asking for comfort. Start by making the routine less harsh.',
  excerpt: 'A simple way to think about dry-feeling skin, from cleansing to the last layer of cream.',
  readTime: '4 min read',
  date: '14 July 2026',
  isoDate: '2026-07-14',
  image: '/lange/paris/editorial/formula-texture-1400.webp',
  imageAlt: 'A rich cream texture with camellia',
  concernUrl: `${CONCERN_PATH}/hydration/`,
  concernLabel: 'Explore care for dryness & hydration',
  sections: [{
    heading: 'Dryness can feel louder than it looks',
    paragraphs: ['Skin can feel tight after washing, rough under make-up or uncomfortable by the end of the day. These are useful signals. They tell you the routine may be taking away more than it gives back.', 'Weather, indoor heating, travel and strong cleansing can all change how skin feels. The first answer is usually a kinder routine, not five new products.']
  }, {
    heading: 'Make the first step gentler',
    paragraphs: ['Use lukewarm water. Avoid scrubbing. After cleansing, apply your next layer while your skin still feels slightly damp if the formula directions allow it. Then finish with a cream that feels right for you.', 'Think of each layer as doing one quiet job: a water-based step adds a fresh feel, while a cream helps keep that comfortable feeling from disappearing too quickly.']
  }, {
    heading: 'Give the change time',
    paragraphs: ['Change one part of your routine at a time when you can. It makes it easier to notice what your skin likes. If dryness comes with cracking, swelling or a rash, seek advice from a qualified health professional.']
  }]
}, {
  slug: 'how-to-read-an-inci-list',
  category: 'ingredients',
  label: 'Ingredients',
  title: 'How to read an ingredient list',
  deck: 'The full list is there to help you make a better choice—not to make you feel like a chemist.',
  excerpt: 'INCI names can look difficult. Here is the simple part: what the order tells you and what it cannot.',
  readTime: '5 min read',
  date: '02 July 2026',
  isoDate: '2026-07-02',
  image: '/lange/paris/editorial/actives-texture-1100.webp',
  imageAlt: 'Cream texture with active ingredient droplets',
  concernUrl: '/version-1-tracked/en/science/',
  concernLabel: 'See how LANGÉ publishes formula information',
  sections: [{
    heading: 'What INCI means',
    paragraphs: ['INCI is the common name used on cosmetic ingredient lists. It is designed to be the same across many countries, so the words may look less familiar than the names you see on the front of a jar.', 'The list is useful because it shows what is in the formula. It is not a scorecard. Long names are not automatically bad, and natural names are not automatically better for every person.']
  }, {
    heading: 'Read the order, not the mood',
    paragraphs: ['Ingredients are normally listed from the highest amount to the lowest, with specific rules for very small amounts. This helps you see the broad shape of a formula: water, oils, humectants, texture ingredients and then smaller additions.', 'A formula is more than one star ingredient. The way ingredients sit together changes how a product feels, spreads and stays comfortable on skin.']
  }, {
    heading: 'Use the list for your own needs',
    paragraphs: ['If you know your skin does not agree with an ingredient, the list lets you check before you buy. If you are unsure about an ingredient or have a known allergy, ask a qualified professional for advice. On Langé product pages, the full INCI should always be easy to find.']
  }]
}, {
  slug: 'day-cream-and-night-cream',
  category: 'routine',
  label: 'Routine notes',
  title: 'Day cream and night cream: do you need both?',
  deck: 'Not always. The best routine is the one that fits your day and your skin.',
  excerpt: 'A clear way to decide whether two creams make sense for you, without adding steps for the sake of it.',
  readTime: '3 min read',
  date: '18 June 2026',
  isoDate: '2026-06-18',
  image: '/lange/paris/editorial/night-application-1100.webp',
  imageAlt: 'Applying night cream in a calm evening ritual',
  concernUrl: `${CONCERN_PATH}/hydration/`,
  concernLabel: 'Find a comfortable daily ritual',
  sections: [{
    heading: 'They can serve different moments',
    paragraphs: ['A day cream often needs to sit well under the rest of your morning. A night cream can feel richer if that is what your skin enjoys when the day is over. But these are choices, not rules.', 'One cream used well is better than two creams bought out of pressure. Let the texture and your own routine decide.']
  }, {
    heading: 'Ask two useful questions',
    paragraphs: ['Does my current cream feel comfortable through the day? And does it leave my skin feeling comfortable when I wake up? If the answer is yes, you may not need to change anything.', 'If mornings feel dry or evenings call for a more cushioning texture, then choosing two formulas may make the routine feel easier rather than longer.']
  }, {
    heading: 'Keep one step steady',
    paragraphs: ['When you try a new cream, keep the rest of your routine familiar for a while. It helps you learn whether the texture suits your skin and makes the decision less confusing.']
  }]
}];
const articles = Array.isArray(window.LANGE_BLOG_ARTICLES) ? window.LANGE_BLOG_ARTICLES.map(note => { const original=originalArticles.find(a=>a.slug===note.slug)||{category:'routine',label:'Routine notes',deck:note.excerpt,readTime:'3 min read',sections:[],concernUrl:'/collections/all',concernLabel:'Explore skincare',image:'/lange/paris/editorial/formulation-worktable-v1-1600.webp',imageAlt:note.title}; return {...original,...note,image:note.image||original.image}; }).sort((a,b)=>originalArticles.findIndex(x=>x.slug===a.slug)-originalArticles.findIndex(x=>x.slug===b.slug)) : originalArticles;
if(window.LANGE_CURRENT_ARTICLE){const note=articles.find(a=>a.slug===window.LANGE_CURRENT_ARTICLE.slug);if(note)Object.assign(note,window.LANGE_CURRENT_ARTICLE);}
const bySlug = Object.fromEntries(articles.map(article => [article.slug, article]));
function articleUrl(slug) {
  return `${JOURNAL_PATH}/${slug}/`;
}
function pagePath() {
  const parts = window.LANGE_ROUTE.replace(/\/+$/, '').split('/');
  const last = parts.at(-1);
  return last === 'journal' ? null : last;
}
function formatArticleCard(article, featured = false) {
  const originalArticle = originalArticles.find(note => note.slug === article.slug);
  const cardLabel = originalArticle ? `Read: ${originalArticle.title}` : article.title;
  return `<article class="lj-card${featured ? ' lj-card--featured' : ''}" data-journal-card data-category="${article.category}" data-search="${escapeHtml(`${article.title} ${article.deck} ${article.label}`.toLowerCase())}">
    <a class="lj-card__image" href="${articleUrl(article.slug)}" aria-label="${escapeHtml(cardLabel)}">
      <img src="${article.image}" alt="${escapeHtml(article.imageAlt)}" loading="lazy" decoding="async">
    </a>
    <div class="lj-card__copy">
      <p class="lj-card__meta"><span>${escapeHtml(article.label)}</span><i></i><time datetime="${article.isoDate}">${escapeHtml(article.date)}</time><i></i><span>${escapeHtml(article.readTime)}</span></p>
      <h2><a href="${articleUrl(article.slug)}">${escapeHtml(article.title)}</a></h2>
      <p>${escapeHtml(article.excerpt)}</p>
      <a class="lj-text-link" href="${articleUrl(article.slug)}">Read the note <span aria-hidden="true">→</span></a>
    </div>
  </article>`;
}
function journalIndex() {
  const feature = articles[0];
  const rest = articles.slice(1);
  return `
    ${header({
    current: 'maison'
  })}
    <main id="main-content" class="lj-main">
      <section class="lj-masthead" aria-labelledby="journal-title">
        <div class="lj-masthead__intro">
          <p class="lj-kicker">The LANGÉ JOURNAL</p>
          <h1 id="journal-title">Useful notes<br><em>for your Skin’s beauty.</em></h1>
          <p>Plain guidance on Face fine lines, The Eye area, Hydration and Natural ingredients. Start with the question you already have; leave with a clearer next step.</p>
        </div>
        <div class="lj-masthead__rule" aria-hidden="true"><span>ORLEANS - COSMETIC VALLEY · FRANCE</span><i></i><span>Since 2008</span></div>
      </section>

      <section class="lj-standard" aria-labelledby="journal-standard-title">
        <div class="lj-standard__intro">
          <p class="lj-kicker">How we write</p>
          <h2 id="journal-standard-title">Useful before<br><em>you buy.</em></h2>
          <p>The Journal is not a product brochure. It is here to make skincare easier to understand, even when the right next step is to use less of it.</p>
        </div>
        <div class="lj-standard__cards">
          <article><span>01</span><h3>Start with the problem</h3><p>Each note begins with a real skin question, not a product name.</p></article>
          <article><span>02</span><h3>Say what we know</h3><p>We explain ingredients and consumer feedback without turning them into medical promises.</p></article>
          <article><span>03</span><h3>Keep the next step honest</h3><p>We link to a concern or formula only when it helps answer the question.</p></article>
        </div>
      </section>

      <section class="lj-feature" aria-labelledby="featured-title" data-journal-feature data-category="${feature.category}" data-search="${escapeHtml(`${feature.title} ${feature.deck} ${feature.label}`.toLowerCase())}">
        <a class="lj-feature__image" href="${articleUrl(feature.slug)}">
          ${mobilePhoto(feature.image, `alt="${escapeHtml(feature.imageAlt)}" fetchpriority="high" decoding="async"`)}
        </a>
        <div class="lj-feature__copy">
          <p class="lj-kicker">Latest note</p>
          <p class="lj-feature__meta">${escapeHtml(feature.label)} <i></i> ${escapeHtml(feature.readTime)}</p>
          <h2 id="featured-title"><a href="${articleUrl(feature.slug)}">${escapeHtml(feature.title)}</a></h2>
          <p>${escapeHtml(feature.deck)}</p>
          <a class="lj-feature__link" href="${articleUrl(feature.slug)}">Read the note <span aria-hidden="true">→</span></a>
        </div>
      </section>

      <section class="lj-directory" aria-labelledby="directory-title">
        <div class="lj-directory__head">
          <div>
            <p class="lj-kicker">Find what you need</p>
            <h2 id="directory-title">Start with the question<br><em>you already have.</em></h2>
          </div>
          <label class="lj-search"><span class="sr-only">Search Journal notes</span><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6.5"></circle><path d="m16 16 4 4"></path></svg><input type="search" data-journal-search placeholder="Search Journal notes" autocomplete="off"></label>
        </div>
        <div class="lj-filters" role="toolbar" aria-label="Filter Journal notes">
          ${categories.map((category, index) => `<button type="button" data-journal-filter="${category.id}" aria-pressed="${index === 0 ? 'true' : 'false'}">${escapeHtml(category.label)}</button>`).join('')}
        </div>
        <p class="lj-results" aria-live="polite" data-journal-results>Showing all ${articles.length} notes</p>
        <div class="lj-grid" data-journal-grid>${rest.map(article => formatArticleCard(article)).join('')}</div>
        <p class="lj-empty" data-journal-empty hidden>No notes match that search yet. Try a different word, or <button type="button" data-journal-reset>see all notes</button>.</p>
      </section>

      <section class="lj-concerns" aria-labelledby="concerns-title">
        <div class="lj-concerns__head">
          <p class="lj-kicker">Need a place to begin?</p>
          <h2 id="concerns-title">Start with how your<br><em>Skin feels today.</em></h2>
          <p>These are simple routes into the collection. They are not a diagnosis.</p>
        </div>
        <nav aria-label="Browse skincare concerns">
          <a href="${CONCERN_PATH}/fine-lines/"><span>01</span><strong>Face fine lines</strong><i aria-hidden="true">→</i></a>
          <a href="${CONCERN_PATH}/eye-area/"><span>02</span><strong>The Eye area</strong><i aria-hidden="true">→</i></a>
          <a href="${CONCERN_PATH}/hydration/"><span>03</span><strong>Hydration</strong><i aria-hidden="true">→</i></a>
          <a href="${CONCERN_PATH}/radiance/"><span>04</span><strong>Radiance</strong><i aria-hidden="true">→</i></a>
        </nav>
      </section>

      <section class="lj-maison" aria-labelledby="maison-reading-title">
        <div><p class="lj-kicker">From the MAISON LANGÉ</p><h2 id="maison-reading-title">The information<br><em>behind the Jar.</em></h2></div>
        <div class="lj-maison__links">
          <a href="${PAGE_PATHS.science}"><span>SCIENCE</span><strong>How we talk about ingredients, testing and claims.</strong><i aria-hidden="true">→</i></a>
          <a href="${PAGE_PATHS.madeInFrance}"><span>MADE in FRANCE</span><strong>Why LANGÉ is formulated and filled at one site near Orléans, FRANCE.</strong><i aria-hidden="true">→</i></a>
        </div>
      </section>
    </main>
    ${footer()}`;
}
function articlePage(article) {
  const related = articles.filter(candidate => candidate.slug !== article.slug).sort((a, b) => a.category === article.category ? -1 : 1).slice(0, 2);
  const body = article.body ? new DOMParser().parseFromString(article.body, 'text/html') : null;
  const headings = body ? [...body.querySelectorAll('h2')].map(e=>e.textContent) : article.sections.map(section => section.heading);
  return `
    ${header({
    current: 'maison'
  })}
    <main id="main-content" class="lja-main">
      <nav class="lja-breadcrumbs" aria-label="Breadcrumb"><a href="${HOME_PATH}">Home</a><span aria-hidden="true">/</span><a href="${JOURNAL_LINK}">Journal</a><span aria-hidden="true">/</span><span>${escapeHtml(article.title)}</span></nav>
      <header class="lja-header">
        <p class="lj-kicker">${escapeHtml(article.label)}</p>
        <h1>${escapeHtml(article.title)}</h1>
        <p class="lja-deck">${escapeHtml(article.deck)}</p>
        <div class="lja-meta"><span>By Langé Editorial</span><i></i><time datetime="${article.isoDate}">${escapeHtml(article.date)}</time><i></i><span>${escapeHtml(article.readTime)}</span></div>
      </header>
      <figure class="lja-hero">${mobilePhoto(article.image, `alt="${escapeHtml(article.imageAlt)}" fetchpriority="high" decoding="async"`)}</figure>
      <div class="lja-layout">
        <aside class="lja-aside" aria-label="On this page">
          <p>In this note</p>
          <ol>${headings.map((heading, index) => `<li><a href="#note-${index + 1}">${String(index + 1).padStart(2, '0')} ${escapeHtml(heading)}</a></li>`).join('')}</ol>
        </aside>
        <article class="lja-prose">
          ${article.body || `<p class="lja-intro">${escapeHtml(article.excerpt)}</p>
          ${article.sections.map((section, index) => `<section id="note-${index + 1}"><h2>${escapeHtml(section.heading)}</h2>${section.paragraphs.map(paragraph => `<p>${escapeHtml(paragraph)}</p>`).join('')}${section.steps ? `<ol class="lja-steps">${section.steps.map((step, stepIndex) => `<li><span>${String(stepIndex + 1).padStart(2, '0')}</span>${escapeHtml(step)}</li>`).join('')}</ol>` : ''}</section>`).join('')}
          <aside class="lja-disclosure"><strong>A useful note, not a diagnosis.</strong><p>Skincare advice should help you notice your skin, not replace professional care. Check the product directions and full ingredient list before use.</p></aside>`}
        </article>
      </div>
      <section class="lja-next" aria-labelledby="next-step-title">
        <div><p class="lj-kicker">If you want to keep looking</p><h2 id="next-step-title">Start with your<br><em>skin concern.</em></h2><p>No quiz required. Choose the concern that sounds most like your skin, then see the relevant formulas.</p></div>
        <a href="${article.concernUrl}">${escapeHtml(article.concernLabel)} <span aria-hidden="true">→</span></a>
      </section>
      <section class="lja-related" aria-labelledby="related-title"><div class="lja-related__head"><p class="lj-kicker">Continue reading</p><h2 id="related-title">More Journal notes.</h2></div><div class="lj-grid">${related.map(item => formatArticleCard(item)).join('')}</div></section>
    </main>
    ${footer()}`;
}
function replaceMeta(name, content) {
  let node = document.querySelector(`meta[name="${name}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.name = name;
    document.head.append(node);
  }
  node.content = content;
}
function setCanonical(path) {
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.append(link);
  }
  link.href = window.langeResolve(`${CDN_ORIGIN}${path.endsWith('/') ? path : `${path}/`}`);
}
function setArticleSeo(article) {
  // Shopify's initial HTML carries the current localized merchant metadata.
  if (window.LANGE_NATIVE_SEO || document.querySelector('[data-lange-native-article-schema]')) return;
  setLangeModuleTitle(`${article.title} | The LANGÉ JOURNAL`);
  replaceMeta('description', article.deck);
  setCanonical(articleUrl(article.slug));
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.deck,
    image: `${CDN_ORIGIN}${article.image}`,
    datePublished: article.isoDate,
    dateModified: article.isoDate,
    author: {
      '@type': 'Organization',
      name: 'LANGÉ Paris'
    },
    publisher: {
      '@type': 'Organization',
      name: 'LANGÉ Paris',
      url: CDN_ORIGIN
    },
    mainEntityOfPage: `${CDN_ORIGIN}${articleUrl(article.slug)}`
  };
  const existing = document.querySelector('[data-journal-schema]');
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.journalSchema = 'true';
  script.textContent = JSON.stringify(jsonLd);
  document.head.append(script);
}
function bindJournalFilters() {
  const buttons = [...document.querySelectorAll('[data-journal-filter]')];
  const search = document.querySelector('[data-journal-search]');
  const cards = [...document.querySelectorAll('[data-journal-card]')];
  const feature = document.querySelector('[data-journal-feature]');
  const results = document.querySelector('[data-journal-results]');
  const empty = document.querySelector('[data-journal-empty]');
  let selected = 'all';
  const sync = () => {
    const term = (search?.value || '').trim().toLowerCase();
    const matches = item => {
      const categoryPass = selected === 'all' || item.dataset.category === selected;
      const searchPass = !term || (item.dataset.search || '').includes(term);
      return categoryPass && searchPass;
    };
    const featureVisible = feature ? matches(feature) : false;
    if (feature) feature.hidden = !featureVisible;
    const visible = cards.filter(card => {
      const show = matches(card);
      card.hidden = !show;
      return show;
    });
    const total = visible.length + Number(featureVisible);
    results.textContent = total === articles.length && selected === 'all' && !term ? `Showing all ${articles.length} notes` : `${total} ${total === 1 ? 'note' : 'notes'} found`;
    empty.hidden = total !== 0;
  };
  buttons.forEach(button => button.addEventListener('click', () => {
    selected = button.dataset.journalFilter || 'all';
    buttons.forEach(item => item.setAttribute('aria-pressed', String(item === button)));
    sync();
  }));
  search?.addEventListener('input', sync);
  document.querySelector('[data-journal-reset]')?.addEventListener('click', () => {
    selected = 'all';
    if (search) search.value = '';
    buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.journalFilter === 'all')));
    sync();
  });
}
function boot() {
  if (/\/blogs\/news\/?$/.test(window.location.pathname) && (!document.getElementById("lange-catalogue-root") || articles.length === 0)) {
    const journalUrl = new URL(window.location.href);
    journalUrl.pathname = journalUrl.pathname.replace(/\/blogs\/news\/?$/, "/blogs/journal");
    window.location.replace(journalUrl.href);
    return;
  }
  const slug = pagePath();
  const article = slug ? bySlug[slug] : null;
  if (slug && !article) {
    window.location.replace(`${JOURNAL_PATH}/`);
    return;
  }
  document.body.classList.add('lj-page');
  document.getElementById('lange-catalogue-root').classList.add('lc-site');
  document.getElementById('lange-catalogue-root').innerHTML = window.langeResolve(article ? articlePage(article) : journalIndex());
  if (article) setArticleSeo(article);else {
    setLangeModuleTitle('The LANGÉ JOURNAL | Premium French Skincare');
    if (!window.LANGE_NATIVE_SEO) setCanonical(JOURNAL_LINK);
  }
  bindHeader();
  bindJournalFilters();
}
boot();